import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import styles from "./ChatWindow.module.scss";
import ChatHeader from "../ChatHeader/ChatHeader";
import Input from "../Input/Input";
import Suggestions from "../Suggestions/Suggestions";
import Slidein from "../slidein/Slidein";
import ChatMessages from "../Messages/ChatMessages";
import { v4 as uuidv4 } from "uuid";

/**
 * CHAT WINDOW COMPONENT
 *
 * Primary chat interface handling:
 * - User-AI message exchange
 * - Course recommendations display
 * - Email capture flow
 * - Session persistence
 * - Responsive viewport adaptations
 *
 * Key Features:
 * - Typing indicators for AI responses
 * - Auto-scroll to new messages
 * - Error-resilient session storage
 * - Development stubs for testing
 * - Slide-in recommendations panel
 *
 * Architecture:
 * - Uses sessionStorage for state persistence
 * - Integrates with external AI API endpoints
 * - Implements accessibility-friendly UI patterns
 * - Supports multi-viewport layouts (mobile/tablet/desktop)
 */

/**
 * Safely retrieves and parses JSON data from sessionStorage.
 * Gracefully handles missing keys and parse errors.
 *
 * @param {string} key - Storage key to retrieve
 * @param {*} fallback - Default value on failure
 * @returns {*} Parsed data or fallback value
 */

function safeLoad(key, fallback) {
  try {
    const s = sessionStorage.getItem(key);
    return s ? JSON.parse(s) : fallback;
  } catch (err) {
    console.warn(`Could not parse sessionStorage['${key}']`, err);
    return fallback;
  }
}

/**
 * Primary chat interface component
 *
 * @param {Object} props - Component properties
 * @param {string} props.viewport - Current viewport type
 * @param {string} props.host - Environment host details
 * @param {Function} props.onClose - Close handler callback
 * @param {string} props.initialMessage - Initial message for auto-send
 * @param {Function} props.clearInitialMessage - Clear initial message callback
 *
 * State Management:
 * - chatMessages: Stores conversation history
 * - recommendations: Course suggestions from AI
 * - slideinOpen: Controls recommendations panel
 * - isTyping: Typing indicator visibility
 * - UI visibility/closing states
 */
const ChatWindow = ({
  viewport,
  host,
  onClose,
  initialMessage,
  clearInitialMessage,
  staticReplies = {},
}) => {
  if (typeof onClose !== "function") {
    console.error("ChatWindow: expected onClose to be a function");
    onClose = () => { };
  }

  // Refs for DOM access and timers
  const contentRef = useRef(null);
  const initialSentRef = useRef(false);
  const tCloseFade = useRef();
  const tCloseUnmount = useRef();
  const sendingRef = useRef(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // State declarations with sessionStorage integration
  const [conversationID] = useState(() => {
    let id = sessionStorage.getItem("conversationID");
    if (!id) {
      id = uuidv4();
      try {
        sessionStorage.setItem("conversationID", id);
      } catch (e) {
        console.warn("Could not write conversationID to sessionStorage", e);
      }
    }
    return id;
  });

  const [chatMessages, setChatMessages] = useState(() =>
    safeLoad("chatMessages", [
      {
        role: "assistant",
        content: "Hej, jag är Boragos AI-medarbetare. Hur kan jag hjälpa dig?",
      },
    ]),
  );

  const [recommendations, setRecommendations] = useState(() =>
    safeLoad("recommendations", []),
  );
  const [recommendationSourceId, setRecommendationSourceId] = useState(null); // Add this
  const [slideinOpen, setSlideinOpen] = useState(
    () => recommendations.length > 0,
  );

  // Visual state management
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  /**
   * Component lifecycle management
   * - Fade-in effect on mount
   * - Session persistence for messages/recommendations
   * - Cleanup for pending timers
   */

  // Fade-in on mount
  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  // Persist chatMessages
  useEffect(() => {
    try {
      sessionStorage.setItem("chatMessages", JSON.stringify(chatMessages));
    } catch { }
  }, [chatMessages]);

  // Persist recommendations
  useEffect(() => {
    try {
      sessionStorage.setItem(
        "recommendations",
        JSON.stringify(recommendations),
      );
    } catch { }
  }, [recommendations]);

  // Cleanup timeouts
  useEffect(
    () => () => {
      clearTimeout(tCloseFade.current);
      clearTimeout(tCloseUnmount.current);
    },
    [],
  );

  /*
   * UI event handlers
   * - handleCloseClick: Clean shutdown sequence
   * - handleSend: Message submission flow
   * - handleSuggestionClick: Quick prompt selection
   */
  const handleCloseClick = () => {
    setIsClosing(true);
    setIsVisible(false);
    if (slideinOpen) {
      setSlideinOpen(false);
    }
    tCloseUnmount.current = setTimeout(onClose, 500);
  };


  /**
   * AI interaction management
   * - sendToAI: Core API communication handler
   * - Manages typing states
   * - Handles API errors and response parsing
   * - // `sendToAI` is a hoisted, stable function and will never change identity,
   * - so it’s safe to omit from the deps array here:
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sendToAI = async (history) => {
    setIsTyping(true);
    await new Promise((r) => setTimeout(r, 1500));

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);

    let data;
    try {
      const res = await fetch(
        "https://europe-central2-borago-chatbot.cloudfunctions.net/app/api/funcCall",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            conversationHistory: history,
            conversationID,
          }),
          signal: controller.signal,
        },
      );
      clearTimeout(timeoutId);

      if (!res.ok) throw new Error(`Status ${res.status}`);
      data = await res.json();
    } catch (err) {
      console.error("AI fetch error:", err);
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Något gick snett, försök senare.",
        },
      ]);
      setIsTyping(false);
      sendingRef.current = false;
      return;
    }

    if (typeof data === "string") {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data,
        },
      ]);
    } else if (data.motivation && data.recommendations) {
      const id = Date.now().toString();
      const newAssistantMsg = {
        id, // This is important
        role: "assistant",
        content: data.motivation,
      };
      const recommendationsWithoutImages = data.recommendations.map(({ image, ...rest }) => rest);

      const recommendationsHidden = {
        type: "hide",
        role: "assistant",
        content: "RECOMMENDATIONS:\n" + JSON.stringify(recommendationsWithoutImages, null, 2),
      };
      console.log(JSON.stringify(recommendationsWithoutImages, 2, null));
      setChatMessages((prev) => [...prev, newAssistantMsg, recommendationsHidden]);
      setRecommendations(data.recommendations);
      setRecommendationSourceId(id); // Track which message triggered recs
      setSlideinOpen(true);
    } else {
      console.warn("Unexpected response:", data);
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Jag fick ett oväntat svar.",
        },
      ]);
    }

    setIsTyping(false);
    sendingRef.current = false;
  };

  /**
 * Sends a message to the AI backend, handles typing state, and
 * prevents duplicate in-flight requests.
 *
 * @param {string} message - The user’s message text.
 */
  const handleSend = useCallback(
    async (message) => {
      // 1) ignore empty
      if (!message.trim()) return;

      // 2) prevent duplicate in-flight requests
      if (sendingRef.current) return;
      sendingRef.current = true;

      // 3) build the outgoing user message
      const userMsg = { role: "user", content: message };

      // 4) tag as course_recommendations if we still have a pending carousel
      if (recommendationSourceId !== null) {
        userMsg.type = "course_recommendations";
      }

      // 5) append it locally
      setChatMessages(prev => [...prev, userMsg]);

      // 6) hide first-click suggestions
      if (showSuggestions && !hasUserInteracted) {
        setHasUserInteracted(true);
        setShowSuggestions(false);
      }

      // 7) send the updated history off to AI
      await sendToAI([...chatMessages, userMsg]);

      // 8) allow new sends
      sendingRef.current = false;
    },
    [
      chatMessages,
      hasUserInteracted,
      showSuggestions,
      recommendationSourceId,
      sendToAI,           // OK because sendToAI is a function declaration (hoisted)
    ]
  );


  // In your handleEmailSubmit function
  const handleEmailSubmit = async (address) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10-second timeout
      console.log("Recommendations for email, ", recommendations)
      const res = await fetch(
        "https://europe-central2-borago-chatbot.cloudfunctions.net/app/api/post-email",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: address, courses: recommendations }),
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Submission failed");
      }

      try {
        sessionStorage.setItem("userEmail", address);
      } catch (storageError) {
        console.error("Session storage error:", storageError);
        throw new Error("Could not save email locally");
      }

      setChatMessages((prev) => [
        ...prev.filter((m) => m.type !== "emailRequest"),
        {
          role: "assistant",
          content: "Tack! Din e-post är nu sparad.",
        },
      ]);
    } catch (err) {
      console.error("Email submission error:", err);

      let errorMessage = "Hoppsan, något gick fel. Försök igen.";

      if (err.name === "AbortError") {
        errorMessage =
          "Begäran tog för lång tid. Kontrollera din anslutning och försök igen.";
      } else if (err.message.includes("locally")) {
        errorMessage =
          "E-posten sparades inte lokalt. Vänligen kontakta support.";
      }

      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          type: "emailRequest",
          content: `${errorMessage}\n\nVill du få dina rekommendationer och chatten på din mail? Samt få information angående nya kurser?\n\nSkriv in din mail nedan!`,
        },
      ]);
    }
  };

  /**
   * Automated behaviors
   * - Initial message auto-send
   * - Recommendation-based email prompts
   * - Message container auto-scroll
   *
   * */

  // Enhanced email prompt useEffect
  useEffect(() => {
    if (recommendations.length === 0) return;

    const hasSubmittedEmail = sessionStorage.getItem("userEmail");
    if (!hasSubmittedEmail) {
      const showEmailRequest = () => {
        setChatMessages((prev) => [
          ...prev.filter((m) => m.type !== "emailRequest"),
          {
            role: "assistant",
            type: "emailRequest",
            content: `Vill du få dina rekommendationer och chatten på din mail? Samt få information angående nya kurser?\n\nSkriv in din mail nedan!`,
          },
        ]);
      };

      // Show typing indicator for 2 seconds before email request
      setIsTyping(true);
      const timer = setTimeout(() => {
        showEmailRequest();
        setIsTyping(false);
      }, 2000);

      return () => {
        clearTimeout(timer);
        setIsTyping(false);
      };
    }
  }, [recommendations]);

  const handleSuggestionClick = (suggestion) => {
    setIsTyping(true); // Show typing indicator immediately

    // Simulate delay for typing indicator
    setTimeout(() => {
      handleSend(suggestion); // Then send the suggestion after delay
    }, 1000); // Adjust delay if needed (this is 1 second delay)
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sendStaticReply = async (message) => {
    setIsTyping(true);

    // Wait for 1.5 seconds to simulate typing
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setChatMessages((prev) => [
      ...prev,
      { role: "assistant", content: staticReplies[message] },
    ]);

    setIsTyping(false);
  };

  useEffect(() => {
    if (!initialMessage || initialSentRef.current) return;

    const canned = staticReplies[initialMessage];
    if (canned) {
      // Add user message immediately
      setChatMessages((prev) => [
        ...prev,
        { role: "user", content: initialMessage },
      ]);

      // Send static reply after delay
      sendStaticReply(initialMessage);

      initialSentRef.current = true;
      clearInitialMessage();
    } else {
      // Existing non-static response handling
      handleSend(initialMessage);
      initialSentRef.current = true;
      clearInitialMessage();
    }
  }, [
    initialMessage,
    clearInitialMessage,
    staticReplies,
    sendStaticReply,
    handleSend,
  ]);

  // auto‑scroll
  useEffect(() => {
    const el = contentRef.current;
    if (el)
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth",
      });
  }, [chatMessages, isTyping]);

  return (
    <div
      className={[
        styles.chatWindowWrapper,
        isClosing ? styles.closing : "",
      ].join(" ")}
    >
      <div
        className={`${styles.chatWindow} ${isVisible ? styles.visible : ""}`}
      >
        <ChatHeader onClose={handleCloseClick} />
        <div className={styles.content} ref={contentRef}>
          <ChatMessages
            messages={chatMessages}
            isTyping={isTyping}
            onEmailSubmit={handleEmailSubmit}
            recommendations={viewport !== "desktop" ? recommendations : []} // Only pass to non-desktop
            recommendationSourceId={
              viewport !== "desktop" ? recommendationSourceId : null
            }
            viewport={viewport}
            host={host}
          />
        </div>
        {!chatMessages.some((m) => m.role === "user") && (
          <Suggestions onClick={handleSuggestionClick} />
        )}
        <Input onSend={handleSend} />
      </div>
      {viewport === "desktop" && (
        <Slidein
          recommendations={recommendations}
          isOpen={slideinOpen}
          setIsOpen={setSlideinOpen}
          closing={isClosing}
          host={host}
        />
      )}
    </div>
  );
};

ChatWindow.propTypes = {
  onClose: PropTypes.func.isRequired,
  initialMessage: PropTypes.string,
  clearInitialMessage: PropTypes.func.isRequired,
};

export default ChatWindow;
